import { yupResolver } from '@hookform/resolvers/yup';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { Box, Container, Dialog, DialogContent, DialogTitle, Grid, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { Block } from 'src/components/Block';
import AppButton from 'src/components/common/AppButton';
import AppLoadingAndErrorWrapper from 'src/components/common/AppLoadingAndErrorWrapper';
import { ConfirmDialog } from 'src/components/custom-dialog';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useAddSessionHearingCouncilMutation } from 'src/graphql';
import { useBoolean } from 'src/hooks/use-boolean';
import { useLocales } from 'src/locales';
import useSchemas from 'src/schemas/hooks/useSchemas';
import {
  AddSessionMutationVariables,
  SessionHearingCouncilDialogPropsType,
} from 'src/types/hearing-council';

const AddSessionHearingCouncilDialog = ({
  open,
  onClose,
}: SessionHearingCouncilDialogPropsType) => {
  // #region States
  const { t } = useLocales();
  const { AddSessionHearingCouncilSchema } = useSchemas();
  const queryClient = useQueryClient();
  const { hearingCouncilId } = useParams();
  const confirm = useBoolean();
  const [dataToSend, setDataToSend] = useState<AddSessionMutationVariables>();
  // #endregion States

  let defaultValues: AddSessionMutationVariables = {
    title: '',
    link: '',
  };

  const methods = useForm<AddSessionMutationVariables>({
    defaultValues,
    resolver: yupResolver(AddSessionHearingCouncilSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = useCallback(
    async (data: AddSessionMutationVariables) => {
      setDataToSend({ hearingCouncilId, ...data });
      confirm.onTrue();
    },
    [confirm]
  );

  // #region Services
  const { mutate: addSession, isLoading: isAdding } = useAddSessionHearingCouncilMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['HearingCouncilLinks'] });
      confirm.onFalse();
      onClose();
    },
  });

  return (
    <>
      <Dialog
        open={open && !confirm.value}
        onClose={onClose}
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', padding: 4 } }}
      >
        <DialogTitle sx={{ pb: 2 }} display="flex">
          {t('dialogs.addSessionHearingCouncil.header')}
        </DialogTitle>
        <DialogContent>
          <Container>
            <AppLoadingAndErrorWrapper
              isLoading={false}
              errorMessage={null}
              sx={{ height: 'auto' }}
            >
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <Stack py={5} spacing={3}>
                      <Block label={t('dialogs.addSessionHearingCouncil.title')}>
                        <RHFTextField name="title" />
                      </Block>
                      <Block label={t('dialogs.addSessionHearingCouncil.link')}>
                        <RHFTextField name="link" />
                      </Block>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AppButton label={t('buttons.add')} isLoading={false} fullWidth type="submit" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AppButton
                      onClick={onClose}
                      label={t('buttons.cancel')}
                      fullWidth
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </FormProvider>
            </AppLoadingAndErrorWrapper>
          </Container>
        </DialogContent>
      </Dialog>
      {/* Confirm */}
      <ConfirmDialog
        open={confirm.value}
        onClose={(event: React.SyntheticEvent<Element, Event>, reason: string) => {
          if (reason && reason === 'backdropClick') {
            confirm.onFalse();
            onClose();
          }
        }}
        content={t('dialogs.confirmMessage', {
          editOrAdd: t('buttons.add').toLowerCase(),
        })}
        icon={<BorderColorOutlinedIcon fontSize="large" sx={{ mx: 'auto' }} />}
        action={
          <>
            <AppButton
              fullWidth
              label={t('publicWords.no')}
              variant="outlined"
              onClick={() => {
                confirm.onFalse();
                onClose();
              }}
            />
            <AppButton
              fullWidth
              label={t('publicWords.yes')}
              isLoading={isAdding}
              onClick={() => addSession(dataToSend!)}
            />
          </>
        }
      />
    </>
  );
};

export default AddSessionHearingCouncilDialog;
